import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';

import {RESET_PROJECT} from './project';
import {fetchTranslatables, fetchTranslatablesSource, saveTranslatables} from './translatable'
import {cloneAO1, deleteAO1, saveAO1} from './translatable/ao1'
import {deleteTR} from './translatable/tr1'
import {deleteVO} from './translatable/vo1'
import {RootState} from '../app/store';


type IObject = TranslationIndex.IndexObject
type Translatable = Editor.Translatable
type TranslatableMap = Editor.TranslatableMap

type TranslationState = {
  source: TranslatableMap
  translated: TranslatableMap
}

const initialState: TranslationState = {
  source: {},
  translated: {},
}

export const CLONE_ASSET = createAsyncThunk(
  'CLONE_ASSET',
  async (
    {entry, source, locale} :
    {entry: IObject, source: Translatable, locale: string}
  ) => {
    const {id} = entry

    const updated = await cloneAO1(source, locale)

    return {id, translatable: updated}
  }
)

export const FETCH_CONTEXT_SOURCE = createAsyncThunk(
  'FETCH_CONTEXT_SOURCE',
  async (
    {entries, locale} : {entries: IObject[], locale: string},
    { getState }
  ) => {
    const { project: { projectId } } = getState() as RootState
    // For AO1, source must come from asset's master locale; TR/VO still use provided master locale
    return fetchTranslatablesSource(entries, locale, projectId)
  }
)

export const FETCH_CONTEXT_TRANSLATED = createAsyncThunk(
  'FETCH_CONTEXT_TRANSLATED',
  async (
    {entries, locale} : {entries: IObject[], locale: string},
    { getState }
  ) => {
    const { project: { projectId } } = getState() as RootState
    return fetchTranslatables(entries, locale, projectId)
  }
)

export const SAVE_CONTEXT_TRANSLATED = createAsyncThunk(
  'SAVE_CONTEXT_TRANSLATED',
  async ({entries, translatables} : {entries: IObject[], translatables: Translatable[]}) => {
    saveTranslatables(entries, translatables)

    return {entries, translatables}
  }
)

export const UPLOAD_ASSET = createAsyncThunk(
  'UPLOAD_ASSET',
  async ({entry, file, translatable} : {entry: IObject, file: File, translatable: Translatable}) => {
    const {id} = entry

    const updated = await saveAO1(translatable, file)

    return {id, translatable: updated}
  }
)

export const REMOVE_ASSET = createAsyncThunk(
  'REMOVE_ASSET',
  async ({entry, translatable}: {entry: IObject, translatable: Translatable}) => {
    const {id} = entry

    await deleteAO1(translatable)

    return {id}
  }
)

export const REMOVE_TR = createAsyncThunk(
  'REMOVE_TR',
  async ({entry, locale}: {entry: IObject, locale: string}, { getState }) => {
    const { project: { projectId } } = getState() as RootState
    const trId = entry.targetId
    await deleteTR(trId, locale, projectId ? { contextId: projectId } : undefined)
    return { id: entry.id }
  }
)

export const REMOVE_VO = createAsyncThunk(
  'REMOVE_VO',
  async (
    {entry, vo}: {entry: IObject, vo: Editor.VoiceOverData},
    { getState }
  ) => {
    const { project: { projectId } } = getState() as RootState
    const ctx = projectId ? { contextId: projectId } : undefined
    // Always remove the VO row for the target locale
    await deleteVO(vo.id, vo.locale, ctx)
    if (vo.ttsEnabled) {
      // In TTS mode, remove the TR for the text so it falls back
      await deleteTR(vo.textId, vo.locale, ctx)
    } else if (vo.assetId) {
      // In Asset mode, remove the translated asset data for this locale
      await deleteAO1({ id: vo.assetId, locale: vo.locale } as any)
    }
    return { id: entry.id }
  }
)

export const editorSlice = createSlice({
  name: 'editor',
  initialState,
  reducers: {
    RESET_EDITOR: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(RESET_PROJECT, () => initialState)
      .addCase(CLONE_ASSET.fulfilled, (state, {payload: {id, translatable}}) => {
        const translated = {...state.translated}
        translated[id] = translatable

        return {
          ...state,
          translated,
        }
      })
      .addCase(FETCH_CONTEXT_SOURCE.fulfilled, (state, {payload: source}) => ({
        ...state,
        source,
      }))
      .addCase(FETCH_CONTEXT_TRANSLATED.fulfilled, (state, {payload: translated}) => ({
        ...state,
        translated,
      }))
      .addCase(SAVE_CONTEXT_TRANSLATED.fulfilled, (state, {payload: {entries, translatables}}) => {
        const translated = {...state.translated}

        entries.forEach(({id}, index) => translated[id] = translatables[index])

        return {
         ...state,
         translated,
        }
      })
      .addCase(UPLOAD_ASSET.fulfilled, (state, {payload: {id, translatable}}) => {
        const translated = {...state.translated}
        translated[id] = translatable

        return {
          ...state,
          translated,
        }
      })
      .addCase(REMOVE_ASSET.fulfilled, (state, {payload: {id}}) => {
        const translated = {...state.translated}
        // Remove the translated entry so the UI shows the upload placeholder
        delete translated[id]

        return {
          ...state,
          translated,
        }
      })
      .addCase(REMOVE_TR.fulfilled, (state, {payload: {id}}) => {
        const translated = {...state.translated}
        delete translated[id]
        return { ...state, translated }
      })
      .addCase(REMOVE_VO.fulfilled, (state, {payload: {id}}) => {
        const translated = {...state.translated}
        delete translated[id]
        return { ...state, translated }
      })
      // After "Save" editor will be closed, so we do not need to update state
      //.addCase(SAVE_CONTEXT_TRANSLATED.fulfilled, ...)
  },
})

export const editorSelector = (state: RootState) => state[editorSlice.name]
export const {RESET_EDITOR} = editorSlice.actions
